import { type AppState, Auth0Provider } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

/**
 * Auth0 SPA provider. Mirrors ezx-admin-web's Auth0ProviderWithNavigate but
 * defaults the post-login landing to "/" (My Trips). The SPA clientId is
 * created by ezx-infra Terraform (auth0 provider) — set VITE_AUTH0_CLIENT_ID.
 */
export function Auth0ProviderWithNavigate({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH0_CALLBACK_URL;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  const onRedirectCallback = (appState: AppState | undefined) => {
    void navigate(appState?.returnTo ?? "/");
  };

  if (!(domain && clientId && redirectUri)) {
    // Env not configured yet (e.g. clientId pending the TF Auth0 app) — render
    // children so the app still boots; login simply won't be available.
    return <>{children}</>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience,
        scope: "openid profile email",
      }}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
